import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, AlertCircle } from 'lucide-react';
import './InlineField.css';

/**
 * Click-to-edit. The one way a single value is changed on a detail page:
 * click the text, change it, Enter saves (⌘↵ in a multi-line field), Esc
 * cancels, blur saves. The parent owns the value and does the write; a
 * rejected save shows its message here and keeps the field open.
 *
 * @param {*}        value
 * @param {Function} onSave      (next) => Promise — throw to reject
 * @param {boolean}  [canEdit=true]
 * @param {string}   [label]     what the field is, for screen readers ("goal")
 * @param {string}   [type]      text | number | date | select
 * @param {Array}    [options]   for select: [{ value, label }]
 * @param {boolean}  [multiline]
 * @param {string}   [placeholder] shown while editing an empty value
 * @param {string}   [emptyText]   shown in place of an empty value
 * @param {Function} [format]      (value) => display text
 */
export default function InlineField({
  value,
  onSave,
  canEdit = true,
  label,
  type = 'text',
  options = [],
  multiline = false,
  placeholder = '',
  emptyText = 'Click to add',
  format,
  maxLength,
  className = '',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const inputRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    inputRef.current.focus();
    if (typeof inputRef.current.select === 'function' && type !== 'select' && type !== 'date') inputRef.current.select();
  }, [editing, type]);

  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const start = () => {
    if (!canEdit || saving) return;
    cancelledRef.current = false;
    setDraft(value === null || value === undefined ? '' : String(value));
    setError('');
    setEditing(true);
  };

  const cancel = () => {
    cancelledRef.current = true;
    setEditing(false);
    setError('');
  };

  const commit = async () => {
    if (cancelledRef.current) return;
    const current = value === null || value === undefined ? '' : String(value);
    let next = type === 'number' ? (draft === '' ? null : Number(draft)) : draft;
    if (typeof next === 'string' && !multiline) next = next.trim();
    if (String(next === null ? '' : next) === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(next);
      setEditing(false);
      setJustSaved(true);
    } catch (err) {
      setError((err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Could not save');
      if (inputRef.current) inputRef.current.focus();
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
    }
  };

  const display = () => {
    if (value === null || value === undefined || value === '') return <span className="inline-field-empty">{emptyText}</span>;
    if (format) return format(value);
    if (type === 'select') {
      const match = options.find((o) => String(o.value) === String(value));
      return match ? match.label : String(value);
    }
    return String(value);
  };

  if (!editing) {
    if (!canEdit) {
      return <span className={`inline-field inline-field-readonly ${className}`}>{display()}</span>;
    }
    return (
      <button
        type="button"
        className={`inline-field ${multiline ? 'inline-field-multiline' : ''} ${justSaved ? 'is-saved' : ''} ${className}`}
        onClick={start}
        aria-label={label ? `Edit ${label}` : 'Edit'}
        title="Click to edit"
      >
        <span className="inline-field-value">{display()}</span>
        {justSaved ? <Check size={13} className="inline-field-icon inline-field-saved" aria-hidden="true" /> : <Pencil size={13} className="inline-field-icon" aria-hidden="true" />}
      </button>
    );
  }

  const common = {
    ref: inputRef,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onKeyDown,
    onBlur: commit,
    disabled: saving,
    'aria-label': label || 'Edit value',
    'aria-invalid': error ? 'true' : undefined,
    placeholder,
    maxLength,
  };

  return (
    <div className={`inline-field-editor ${className}`}>
      {type === 'select' ? (
        <select className="form-select" {...common}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : multiline ? (
        <textarea className="form-textarea" rows={3} {...common} />
      ) : (
        <input className="form-input" type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'} {...common} />
      )}
      <div className="inline-field-hint">
        {error ? (
          <span className="inline-field-error" role="alert"><AlertCircle size={12} aria-hidden="true" /> {error}</span>
        ) : saving ? (
          <span>Saving…</span>
        ) : (
          <span>{multiline ? '⌘↵ to save' : 'Enter to save'} · Esc to cancel</span>
        )}
      </div>
    </div>
  );
}
